import React, { useState, useEffect, useRef } from 'react';
import request from 'utils/request_ajax';
import { Icon } from 'ui';
import { routes } from 'constants/config';
import Text, { TEXT_VARIANTS } from 'components/ui/Typography/Text';
import { debounce } from 'lodash/fp';
import { BetaFeedback } from 'components/BetaProgram';
import EMAILS from 'constants/emails';
import EventList from './EventList';

const SelectEvent = ({ setSelectedEvent }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSort, setSelectedSort] = useState('0');
  const [showLoadMore, setShowLoadMore] = useState(false);
  const [isSearchResult, setIsSearchResult] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [emptyState, setEmptyState] = useState('');

  const searchInputRef = useRef(null);
  const sortSelectRef = useRef(null);

  const sortOptions = [
    t('lessons.editor.event.recently_updated'),
    t('lessons.editor.event.name_a_z'),
    t('lessons.editor.event.name_z_a'),
  ];

  const renderSortOptions = () => {
    return sortOptions.map((label, i) => (
      // eslint-disable-next-line react/no-array-index-key
      <option key={i} value={i} children={label} aria-label={label} />
    ));
  };

  const getSortParams = value => {
    if (value === '0') {
      return 'sort[direction]=DESC&sort[column]=date_updated';
    }
    if (value === '1') {
      return 'sort[direction]=ASC&sort[column]=name';
    }
    return 'sort[direction]=DESC&sort[column]=name';
  };

  const handleSortChange = event => {
    const { value } = event.target;
    const sortParams = getSortParams(value);
    setLoading(true);
    setSelectedSort(value);
    setPage(1);

    request
      .get(
        `${routes.TRAINING.EVENTS}?${sortParams}&past_or_upcoming=Upcoming%20Events&page=1&per_page=12`,
      )
      .then(res => {
        if (res) {
          const { content, pageCount } = res;
          setEvents(content.filter(eventItem => eventItem.next_session));
          setTotalPages(pageCount);
          setLoading(false);
        }
      });
  };

  useEffect(() => {
    if (isSearchResult) setIsSearchResult(false);
    request
      .get(
        `${routes.TRAINING.EVENTS}?sort[direction]=DESC&sort[column]=date_updated&past_or_upcoming=Upcoming%20Events&page=1&per_page=12`,
      )
      .then(res => {
        if (res) {
          const { content, pageCount } = res;
          if (!content.length) {
            setEmptyState('initial');
          } else {
            setEvents(content.filter(eventItem => eventItem.next_session));
          }
          setTotalPages(pageCount);
          setLoading(false);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchQuery = useRef(
    debounce(300, event => {
      const { value } = event.target;
      setLoading(true);
      setSelectedSort('0');
      setPage(1);
      setIsSearchResult(true);
      request
        .get(
          `${routes.TRAINING.EVENTS}?query=${value}&sort[direction]=DESC&past_or_upcoming=Upcoming%20Events&page=1&per_page=12`,
        )
        .then(res => {
          if (res) {
            const { content, pageCount } = res;
            if (content.length) {
              setEmptyState('search');
            }
            setEvents(content.filter(eventItem => eventItem.next_session));
            setTotalPages(pageCount);
            setLoading(false);
          }
        });
    }),
  ).current;

  const getNextPage = () => {
    if (!showLoadMore) {
      setShowLoadMore(true);

      const { value } = sortSelectRef.current;
      const sortParams = getSortParams(value);

      const route = isSearchResult
        ? `${routes.TRAINING.EVENTS}?query=${
            searchInputRef.current?.value
          }&${sortParams}&past_or_upcoming=Upcoming%20Events&page=${
            page + 1
          }&per_page=12`
        : `${
            routes.TRAINING.EVENTS
          }?${sortParams}&past_or_upcoming=Upcoming%20Events&page=${
            page + 1
          }&per_page=12`;
      request.get(route).then(res => {
        if (res) {
          const { content } = res;
          setPage(prev => prev + 1);
          setEvents(prev => [...prev, ...content]);
          setShowLoadMore(false);
        }
      });
    }
  };

  return (
    <>
      <div className="EditorElement-header p-32 border-bottom-0 pl-32 pr-32 pb-16 pt-32">
        <Text className="mb-8 d-ib" variant={TEXT_VARIANTS.SUBHEADER}>
          {t('lessons.editor.event.select_event')}
        </Text>
        <input
          className="NewInput NewInput--light"
          type="text"
          placeholder={t('lessons.editor.event.search_event')}
          onChange={handleSearchQuery}
          ref={searchInputRef}
        />
        <div className="AssignModal-sortButton d-f jc-e mt-16">
          <select
            aria-label="Sort Events"
            className="NewButton NewButton--white"
            onChange={handleSortChange}
            value={selectedSort}
            children={renderSortOptions()}
            ref={sortSelectRef}
          />
          <div className="AssignModal-sortIcon">
            <Icon name="sort" />
          </div>
        </div>
      </div>
      <div className="EditorElement-body EventList pl-32 pr-32 pb-16">
        <EventList
          events={events}
          loading={loading}
          setSelectedEvent={setSelectedEvent}
          page={page}
          totalPages={totalPages}
          getNextPage={getNextPage}
          showLoadMore={showLoadMore}
          emptyState={emptyState}
          previewing
        />
        <BetaFeedback
          recipient={EMAILS.EVENTS_FEEDBACK}
          betaFeatureName="Events in Lessons"
          className="mt-16"
        />
      </div>
    </>
  );
};

export default SelectEvent;
